/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 08/09/22
 */

import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import { namedMemo } from '@/utils/namedMemo';
import { PartialRecord } from '@/utils/typeHelpers';
import { isEqual } from 'lodash';
import React, { ComponentType, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

type ListenerCallback = () => void;
type UnsubscribeFunc = () => void;
type SubscribeFunc = (onStoreChange: ListenerCallback) => UnsubscribeFunc;

export type TouchedState = PartialRecord<string, boolean>;

interface ITouchedStateContext {
    getState: () => TouchedState;
    subscribe: SubscribeFunc;
    setTouched: (id: string, touched?: boolean) => void;
}

export const TouchedStateContext = createUnprovisionedContext<ITouchedStateContext>('TouchedSelectorContext');

export const TouchedStateProvider = ({ children }: { children: JSX.Element }) => {
    const [touchedState, setTouchedState] = useState<TouchedState>({});

    const touchedRef = useRef(touchedState);
    useMemo(() => {
        touchedRef.current = touchedState;
    }, [touchedState]);
    const getState = useCallback(() => touchedRef.current, [touchedRef]);

    const [listeners, setListeners] = useState<ListenerCallback[]>([]);
    const subscribe = useCallback((newListener: ListenerCallback): UnsubscribeFunc => {
        setListeners((existingListeners) => [...existingListeners, newListener]);
        return () => {
            setListeners((oldListeners) => {
                return oldListeners.filter((l) => l !== newListener);
            });
        };
    }, []);

    useEffect(() => {
        listeners.forEach((listener) => listener());
    }, [touchedState, listeners]);

    const setSingleTouched = useCallback((id: string, isTouched = true) => {
        setTouchedState((prevTouchedItemsList) => {
            return prevTouchedItemsList[id] === isTouched
                ? prevTouchedItemsList
                : {
                      ...prevTouchedItemsList,
                      [id]: isTouched,
                  };
        });
    }, []);

    const context: ITouchedStateContext = useMemo(
        () => ({
            getState,
            subscribe,
            setTouched: setSingleTouched,
        }),
        [getState, subscribe],
    );

    return <TouchedStateContext.Provider value={context}>{children}</TouchedStateContext.Provider>;
};

/**
 * Implementation of useSyncExternalStoreWithSelector to prevent re-rendering
 * components when touched values haven't changed
 * @param selector
 */
export const useTouchedSelector = <T,>(selector: (state: TouchedState) => T) => {
    const touchedContext = useContext(TouchedStateContext);
    return useSyncExternalStoreWithSelector(
        touchedContext.subscribe,
        touchedContext.getState,
        touchedContext.getState,
        selector,
        isEqual,
    );
};

/**
 * Get touched state of id and setter function
 * @param id
 */
export const useTouched = (id?: string) => {
    const touchedContext = useContext(TouchedStateContext);
    return {
        setTouched: touchedContext.setTouched,
        isTouched: useTouchedSelector((state) => (isNullish(id) ? false : !!state[id])),
    };
};

/**
 * Wrap component with TouchedStateProvider
 * @param WrappedComponent
 */
export const withTouchedContext = <T extends object>(WrappedComponent: ComponentType<T>) =>
    namedMemo('withTouchedContext', (props: T) => {
        return (
            <TouchedStateProvider>
                <WrappedComponent {...props} />
            </TouchedStateProvider>
        );
    });
